import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { NotificationActions } from '../../common/actions/NotificationActions';
import ProjectPackage from '../types/project/ProjectPackage';
import ProjectParticipant from '../types/project/ProjectParticipant';
import ProjectTranslation from '../types/project/ProjectTranslation';
import ProjectJobMatchScale from '../types/jobMatch/ProjectJobMatchScale';
import ProjectJobMatchQuestionnaire from '../types/jobMatch/ProjectJobMatchQuestionnaire';
import IAdminProject from '../types/IAdminProject';
import { ParticipantPayload } from './Admin360ParticipantService';

const BASE_URL = '/admin/products';

const createUrl = (productId: number, projectId: number) =>
    `${BASE_URL}/${productId}/projects/${projectId}`;

const AdminProjectService = {
    async createProject(
        payload: { productId: number; name: string; locale?: string },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any, { name: string }>> {
        const { productId, name, locale } = payload;
        try {
            return await RequestService.post<any>(
                `${BASE_URL}/${productId}/projects`,
                {
                    name,
                    locale,
                },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se vytvorit projekt'),
            ); // TODO: translate

            throw e;
        }
    },
    async getProject(
        payload: {
            productId: number;
            projectId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId } = payload;

        try {
            return await RequestService.get<any>(
                `${BASE_URL}/${productId}/projects/${projectId}`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ziskat nastaveni'),
            ); // TODO: translate

            throw e;
        }
    },
    async saveProject(
        payload: {
            productId: number;
            projectId: number;
            name?: string;
            note?: string;
            consentText?: string;
            enableProjectName?: boolean;
            enableResults?: boolean;
            enableAdditionalDescription?: boolean;
            locale?: string;
            unit?: string;
            level?: string;
            position?: string;
            primaryLanguage?: string;
            phase?: number;
            state?: 0 | 1;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const {
            productId,
            projectId,
            level,
            unit,
            position,
            primaryLanguage,
            ...rest
        } = payload;

        const body: any = { primaryLanguage, ...rest };
        if (level || unit || position) {
            body.config = {};

            if (level) {
                body.config.level = level;
            }
            if (unit) {
                body.config.unit = unit;
            }
            if (position) {
                body.config.position = position;
            }
        }

        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}`,
                body,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit nastaveni'),
            ); // TODO: translate

            throw e;
        }
    },
    async selectPackage(
        payload: { id: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any, any>> {
        try {
            return await RequestService.post<any>(`${BASE_URL}`, {
                id: payload.id,
            });
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se zvolit balicek'),
            ); // TODO: translate

            throw e;
        }
    },
    async getPackages(
        payload: { productId: number; projectId: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectPackage[]>> {
        const { productId, projectId } = payload;
        try {
            return await RequestService.get<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/packages`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist balicky'),
            ); // TODO: translate

            throw e;
        }
    },
    async getQuestionnaires(
        payload: { productId: number; projectId: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectJobMatchQuestionnaire[]>> {
        const { productId, projectId } = payload;
        try {
            return await RequestService.get<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/questionnaires`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist dotazniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async selectQuestionnaires(
        payload: {
            productId: number;
            projectId: number;
            selectedQuestionnaires: { id: number }[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<IAdminProject>> {
        const { productId, projectId, selectedQuestionnaires } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}`,
                { selectedQuestionnaires },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit dotazniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async updateQuestionnaires(
        payload: {
            productId: number;
            projectId: number;
            questionnaires: { importance: number; id: number }[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectJobMatchQuestionnaire[]>> {
        const { productId, projectId, questionnaires } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/questionnaires`,
                questionnaires,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit dotazniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async getParticipants(
        payload: { productId: number; projectId: number; limit?: number },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectParticipant[]>> {
        const { productId, projectId, limit } = payload;
        try {
            return await RequestService.get<ProjectParticipant[]>(
                `${BASE_URL}/${productId}/projects/${projectId}/participants${limit ? '?limit=' + limit : ''}`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist ucastniky'),
            ); // TODO: translate

            throw e;
        }
    },
    async addParticipant(
        payload: {
            productId: number;
            projectId: number;
            id?: number;
            email?: string;
            firstName: string;
            lastName: string;
            userIdentifier?: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectParticipant>> {
        const {
            productId,
            projectId,
            id,
            email,
            firstName,
            lastName,
            userIdentifier,
        } = payload;
        try {
            return await RequestService.post<any, ProjectParticipant>(
                `${BASE_URL}/${productId}/projects/${projectId}/participants`,
                { id, email, firstName, lastName, userIdentifier },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se pridat ucastnika'),
            ); // TODO: translate

            throw e;
        }
    },
    async getTranslations(
        payload: { productId: number; projectId: number; locale: string },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectTranslation[]>> {
        const { productId, projectId, locale } = payload;
        try {
            return await RequestService.get<ProjectTranslation[]>(
                `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist preklady'),
            ); // TODO: translate

            throw e;
        }
    },
    async getTranslation(
        payload: { productId: number; projectId: number; locale: string },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectTranslation>> {
        const { productId, projectId, locale } = payload;
        try {
            return await RequestService.get<ProjectTranslation>(
                `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist preklad'),
            ); // TODO: translate

            throw e;
        }
    },
    async addLanguage(
        payload: {
            productId: number;
            projectId: number;
            locale: string;
            locales: string[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, locale, locales } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}`,
                { locales: [...locales, locale] },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit jazyk'),
            ); // TODO: translate

            throw e;
        }
    },
    async removeLanguage(
        payload: {
            productId: number;
            projectId: number;
            locale: string;
            locales: string[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, locale, locales } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}`,
                { locales: locales.filter((l) => l !== locale) },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se odebrat jazyk'),
            );

            throw e;
        }
    },
    async saveTranslations(
        payload: {
            productId: number;
            projectId: number;
            locale: string;
            translations: ProjectTranslation[];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, locale, translations } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/translations?locale=${locale}`,
                translations,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit preklad'),
            ); // TODO: translate

            throw e;
        }
    },
    async saveTranslation(
        payload: {
            productId: number;
            projectId: number;
            locale: string;
            id: number;
            value: string;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId, id, locale, value } = payload;
        try {
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/translations/${id}?locale=${locale}`,
                { value },
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ulozit preklad'),
            ); // TODO: translate

            throw e;
        }
    },
    async getQuestionnaireScales(
        payload: {
            productId: number;
            projectId: number;
            questionnaireId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectJobMatchScale[]>> {
        const { productId, projectId, questionnaireId } = payload;
        try {
            return await RequestService.get<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/questionnaires/${questionnaireId}/scales`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se nacist škaly'),
            ); // TODO: translate

            throw e;
        }
    },
    async updateQuestionnaireScales(
        payload: {
            productId: number;
            projectId: number;
            questionnaireId: number;
            scaleId: number;
            importance: number;
            selectedRange: [number, number];
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<ProjectJobMatchScale>> {
        const {
            productId,
            projectId,
            questionnaireId,
            scaleId,
            importance,
            selectedRange,
        } = payload;
        try {
            const body: any = { importance };
            if (selectedRange && selectedRange.length > 0) {
                body.selectedRange = selectedRange;
            }
            return await RequestService.patch<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/questionnaires/${questionnaireId}/scales/${scaleId}`,
                body,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se upravit škalu'),
            ); // TODO: translate

            throw e;
        }
    },
    async getProjectStatistics(
        payload: {
            productId: number;
            projectId: number;
        },
        thunkApi: GetThunkAPI<any>,
    ): Promise<RequestServiceResponse<any>> {
        const { productId, projectId } = payload;

        try {
            return await RequestService.get<any>(
                `${BASE_URL}/${productId}/projects/${projectId}/statistics`,
            );
        } catch (e) {
            thunkApi.dispatch(
                NotificationActions.error('nepodarilo se ziskat statistiky'),
            ); // TODO: translate

            throw e;
        }
    },
    deleteProject,
    copyProject,
    anonymizeProject,
    deleteParticipant,
};

async function deleteProject(
    payload: { productId: number; projectId: number },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse> {
    const { productId, projectId } = payload;
    try {
        return await RequestService.delete(createUrl(productId, projectId));
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat projekt'),
        ); // TODO: translate

        throw e;
    }
}

async function copyProject(
    payload: { productId: number; projectId: number; name: string },
    thunkApi: GetThunkAPI<any>,
) {
    const { productId, projectId, name } = payload;

    try {
        return await RequestService.post<any>(
            `${BASE_URL}/${productId}/projects/${projectId}/copy`,
            { name },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se ulozit projekt'),
        ); // TODO: translate

        throw e;
    }
}

async function anonymizeProject(
    payload: { productId: number; projectId: number },
    thunkApi: GetThunkAPI<any>,
) {
    const { productId, projectId } = payload;

    try {
        return await RequestService.post<any>(
            `${BASE_URL}/${productId}/projects/${projectId}/anonymize`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se anonymizovat projekt'),
        ); // TODO: translate

        throw e;
    }
}

async function deleteParticipant(
    payload: ParticipantPayload & { productId: number },
    thunkApi: GetThunkAPI<any>,
) {
    const { productId, projectId, participantId } = payload;
    try {
        return await RequestService.delete(
            `/admin/products/${productId}/projects/${projectId}/participants/${participantId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat ucastnika'),
        ); // TODO: translate

        throw e;
    }
}

export default AdminProjectService;
